/// <reference types="vite/client" />

import { useI18n } from 'vue-i18n';
import { computed } from 'vue';
import { useAppStore } from 'store/modules/appStore';
import { getTheme } from 'web_v2/src/styles/theme';
import { getImageVersion } from 'config/imageVersion';
/**
 * @author 胡小右
 * @date 2022-06-25 20:15:09
 * @desc 获取图片资源
 */

export default function useImage() {
    const appStore = useAppStore();
    const systemData = computed(() => appStore.getSystemData);

    const imgVerson = getImageVersion(); // 使用统一的版本号配置

    return {
        getI18nImg: (name: string) => {
            const { locale } = useI18n();
            return `/assets/images/${locale.value}/${name}?v=${imgVerson}`;
        },

        getImg: (name: string) => {
            // 默认主题(green)不需要目录前缀
            return `/assets/images/${name}?v=${imgVerson}`;
        },

        getThemeImg: (name: string) => {
            const theme = getTheme();
            // 默认主题(green)不需要目录前缀
            if (theme === 'green') {
                return `/assets/images/${name}?v=${imgVerson}`;
            } else if (theme === 'red_v2') {
                return `/assets/images_red/${name}?v=${imgVerson}`;
            } else {
                // 非默认主题使用 images_[theme] 目录
                return `/assets/images_${theme}/${name}?v=${imgVerson}`;
            }
        },

        getSvg: (name: string) => {
            return `/assets/svg/${name}?v=${imgVerson}`;
        },

        // 获取商户对应的图片
        getAliasImg: (name: string) => {
            const aliasNum = systemData.value?.aliasNum;
            switch (aliasNum) {
                case '306':
                    return `/assets/images/306/${name}?v=${imgVerson}`;
                default:
                    return `/assets/images/306/${name}?v=${imgVerson}`;
            }
        },

        getAliasVipImage: (name: string) => {
            const aliasNum = systemData.value?.aliasNum;
            switch (aliasNum) {
                case '610':
                    return `/assets/vip/610/${name}?v=${imgVerson}`;
                default:
                    return `/assets/vip/default/${name}?v=${imgVerson}`;
            }
        },

        // 获取图片版本号
        getImageVersion: () => imgVerson,
    };
}

/**
 * 动态设置CSS自定义属性中的图片URL版本号
 * 这个函数会遍历所有的CSS变量，找到图片URL并添加版本号
 */
export function setCSSImageVersion() {
    const imgVersion = getImageVersion(); // 使用统一的版本号配置

    // 获取根元素的计算样式
    const rootElement = document.documentElement;
    const computedStyle = getComputedStyle(rootElement);

    // 获取所有CSS自定义属性
    const allProperties = Array.from(document.styleSheets)
        .filter(sheet => {
            try {
                return sheet.cssRules;
            } catch (e) {
                return false;
            }
        })
        .flatMap(sheet => Array.from(sheet.cssRules))
        .filter(rule => rule instanceof CSSStyleRule)
        .flatMap(rule => Array.from(rule.style))
        .filter(prop => prop.startsWith('--'))
        .filter((prop, index, arr) => arr.indexOf(prop) === index); // 去重

    // 更新包含图片URL的CSS变量
    allProperties.forEach(prop => {
        const value = computedStyle.getPropertyValue(prop).trim();

        // 检查是否是图片URL
        if (value.includes('url(') && value.includes('/assets/images')) {
            // 移除已存在的版本号参数
            let newValue = value.replace(/\?v=[^'")]+/g, '');

            // 添加新的版本号
            newValue = newValue.replace(/url\((['"]?)([^'")]+)(['"]?)\)/g, (match, quote1, url, quote2) => {
                if (url.includes('/assets/images')) {
                    const separator = url.includes('?') ? '&' : '?';
                    return `url(${quote1}${url}${separator}v=${imgVersion}${quote2})`;
                }
                return match;
            });

            // 设置更新后的CSS变量
            rootElement.style.setProperty(prop, newValue);
        }
    });
}
